"use client";
import { useState } from "react";
import { CheckCircle2, CircleAlert, ExternalLink, Play, PlayCircle, ShieldCheck } from "lucide-react";
import { useI18n, useT } from "@/lib/i18n";
import { useApi } from "@/lib/hooks/use-api";
import { post } from "@/lib/api/client";
import { RequireAuth } from "@/components/auth";
import { Badge, Button, DataState, Empty, ErrorState, PageHeading, Progress } from "@/components/ui";

// «Фишингтен қорғану» сабағының тілдік видео-лекциялары (YouTube embed).
const PHISHING_VIDEOS: Record<string, string> = {
  kk: "q5OWUnGN1_Y", // Қазақстан Ұлттық Банкі: MoneyTime – қаржылық алаяқтық
  ru: "JHLUdQi7U_Q", // МОШЕЛОВКА.РФ: Что такое фишинг и как от него защититься
  en: "1jfm2E_wvBo", // Phishing Awareness Training 2025
  ko: "weQOBzzpZW4", // 전직 검사가 말하는 사기 패턴
};

interface CitizenScenario {
  id: string;
  title: string;
  description: string;
  direction: string;
  difficulty: string;
  duration_minutes: number;
  type: string;
}
interface CitizenOption {
  id: string;
  text: string;
}
interface CitizenTask {
  id: string;
  title: string;
  description: string;
  icon?: string;
  tag?: string;
  kind: string | null;
  options: CitizenOption[] | null;
  answer: {
    answer: string | null;
    status: string;
    feedback: string | null;
    attempts: number;
  } | null;
}
interface CitizenSession {
  id: string;
  scenario_id: string;
  scenario_title: string | null;
  scenario_type: string | null;
  status: string;
  result: { score: number; total: number; pct: number } | null;
  tasks: CitizenTask[];
}

function PracticePlayer({
  scenario,
  onBack,
}: {
  scenario: CitizenScenario;
  onBack: () => void;
}) {
  const t = useT();
  const { lang } = useI18n();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string>();
  const [selected, setSelected] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [starting, setStarting] = useState(true);

  const req = useApi<CitizenSession>(sessionId ? `/practices/sessions/${sessionId}?lang=${lang}` : null);

  // session ашу (идемпотентті: ашық болса — солды қайтарады)
  if (starting && !sessionId) {
    (async () => {
      try {
        const r = await post<{ session_id: string }>("/practices/sessions", {
          scenario_id: scenario.id,
        });
        setSessionId(r.session_id);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setStarting(false);
      }
    })();
  }

  const session = req.data;
  const answeredCount = session ? session.tasks.filter((x) => x.answer).length : 0;
  const current = session ? session.tasks[answeredCount] : null;
  const done =
    session?.status === "COMPLETED" || session?.status === "PASSED";

  const submit = async (optionId: string) => {
    if (!session || submitting) return;
    setSubmitting(true);
    setError(undefined);
    try {
      const r = await post<{ feedback: string }>(
        `/practices/sessions/${session.id}/answers`,
        { task_id: current!.id, answer: optionId },
      );
      setSelected(optionId);
      // feedback көрсетілген соң келесі сұраққа
      setTimeout(() => {
        req.reload();
        setSelected(null);
      }, 4200);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="panel citizen-player">
      <button className="back-link" onClick={onBack}>
        ← {t("citizen.back")}
      </button>
      <h2>{scenario.title}</h2>
      {starting && <p className="panel-sub">{t("citizen.loading")}</p>}
      {error && (
        <p className="form-error">
          <CircleAlert size={15} /> {error}
        </p>
      )}
      {session && !done && current && (
        <>
          {PHISHING_VIDEOS[lang] && scenario.id === "cit-phishing-defense" && (
            <div className="citizen-video">
              <div className="citizen-video-head">
                <span className="citizen-video-badge">
                  <Play size={12} />
                  {t("citizen.video_lesson")}
                </span>
                <a
                  className="citizen-video-link"
                  href={`https://www.youtube.com/watch?v=${PHISHING_VIDEOS[lang]}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("citizen.video_full")} <ExternalLink size={12} />
                </a>
              </div>
              <div className="citizen-video-frame">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${PHISHING_VIDEOS[lang]}?rel=0`}
                  title={t("citizen.video_lesson")}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          )}
          <Progress
            value={Math.round((answeredCount / session.tasks.length) * 100)}
            label={`${answeredCount + 1} / ${session.tasks.length}`}
          />
          <div className="citizen-task-head">
            {current.icon && <span className="citizen-task-icon" aria-hidden="true">{current.icon}</span>}
            <div className="citizen-task-meta">
              {current.tag && <span className="citizen-task-tag">{current.tag}</span>}
              <h3>{current.title}</h3>
            </div>
          </div>
          <p className="citizen-situation">{current.description}</p>
          <div className="citizen-options">
            {(current.options ?? []).map((o) => {
              const ans = current.answer;
              const isAnswered = ans != null;
              const isPicked = selected === o.id || ans?.answer === o.id;
              let cls = "citizen-option";
              if (isAnswered) {
                const correctId = ans.status === "CORRECT" ? ans.answer : undefined;
                if (o.id === correctId) cls += " correct";
                else if (isPicked) cls += " wrong";
                else cls += " dim";
              }
              return (
                <button
                  key={o.id}
                  className={cls}
                  disabled={isAnswered || submitting}
                  onClick={() => submit(o.id)}
                >
                  {o.text}
                </button>
              );
            })}
          </div>
          {current.answer && (
            <div
              className={
                current.answer.status === "CORRECT"
                  ? "citizen-feedback ok"
                  : "citizen-feedback bad"
              }
            >
              {current.answer.status === "CORRECT" ? (
                <CheckCircle2 size={17} />
              ) : (
                <CircleAlert size={17} />
              )}
              <span>{current.answer.feedback}</span>
            </div>
          )}
        </>
      )}
      {session && done && session.result && (
        <div className="citizen-result">
          <ShieldCheck size={40} />
          <h3>
            {t("citizen.result")}: {session.result.score}/{session.result.total}
          </h3>
          <p className="panel-sub">{t("citizen.result_note")}</p>
          <Button variant="primary" onClick={onBack}>
            {t("citizen.done")}
          </Button>
        </div>
      )}
    </div>
  );
}

export function CitizenPage() {
  const t = useT();
  const { lang } = useI18n();
  const [active, setActive] = useState<CitizenScenario | null>(null);
  const list = useApi<{ scenarios: CitizenScenario[] }>(
    `/practices/scenarios?direction=CITIZEN&lang=${lang}`,
  );

  return (
    <>
      <PageHeading
        eyebrow="CITIZEN · 2.0 §7"
        title={t("citizen.title")}
        description={t("citizen.subtitle")}
      />
      <RequireAuth>
        {active ? (
          <PracticePlayer key={active.id} scenario={active} onBack={() => setActive(null)} />
        ) : (
          <DataState loading={list.loading} error={list.error} retry={list.reload}>
            {!list.loading && !list.error && !list.data?.scenarios.length && (
              <Empty title={t("citizen.empty")} text="" />
            )}
            <div className="citizen-grid">
              {list.data?.scenarios.map((s) => (
                <button
                  key={s.id}
                  className="citizen-card"
                  onClick={() => setActive(s)}
                >
                  <div className="citizen-card-head">
                    <Badge value={s.difficulty} />
                    <span className="citizen-dur">{s.duration_minutes} мин</span>
                  </div>
                  <h3>{s.title}</h3>
                  <p>{s.description}</p>
                  <span className="citizen-start">
                    <PlayCircle size={16} /> {t("citizen.start")}
                  </span>
                </button>
              ))}
            </div>
          </DataState>
        )}
      </RequireAuth>
    </>
  );
}
